import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowClockwise } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import EmergencyButton from "../components/EmergencyButton";
import { EPDS_QUESTIONS, evaluateEpds, EPDS_ZONE_META } from "../data/staticData";
import { getJSON, setJSON, K } from "../lib/storage";

export default function Epds() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(Array(EPDS_QUESTIONS.length).fill(null));
  const [result, setResult] = useState(null);

  const q = EPDS_QUESTIONS[step];
  const progress = ((step + (result ? 1 : 0)) / EPDS_QUESTIONS.length) * 100;

  const choose = (value) => {
    const next = [...answers];
    next[step] = value;
    setAnswers(next);
    setTimeout(() => {
      if (step < EPDS_QUESTIONS.length - 1) {
        setStep(step + 1);
      } else {
        const res = evaluateEpds(next);
        setResult(res);
        const rec = { date: new Date().toISOString().slice(0, 10), score: res.score, zone: res.zone };
        const hist = [...getJSON(K.epds, []).filter((r) => r.date !== rec.date), rec];
        setJSON(K.epds, hist);
      }
    }, 180);
  };

  const restart = () => {
    setStep(0);
    setAnswers(Array(EPDS_QUESTIONS.length).fill(null));
    setResult(null);
  };

  if (result) {
    const meta = EPDS_ZONE_META[result.zone];
    return (
      <div>
        <PageHeader title="Votre résultat" back />
        <div className="px-6">
          <div className="rounded-[28px] p-7 text-center" style={{ backgroundColor: `${meta.color}1F` }}>
            <div
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-full font-display text-3xl font-semibold text-white"
              style={{ backgroundColor: meta.color }}
              data-testid="epds-result-score"
            >
              {result.score}
            </div>
            <p className="mt-4 font-display text-2xl font-medium text-[#36302B]">Zone : {meta.label}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-[#36302B]/75">{meta.message}</p>
          </div>

          {result.atRisk && (
            <div className="mt-5">
              <EmergencyButton />
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3">
            <Link
              to="/aide"
              data-testid="epds-to-aide"
              className="flex items-center justify-center gap-2 rounded-full bg-[#C57B67] py-4 text-[15px] font-bold text-white transition-colors hover:bg-[#B56A63]"
            >
              Trouver de l'aide
            </Link>
            <button
              onClick={restart}
              data-testid="epds-restart"
              className="flex items-center justify-center gap-2 rounded-full border border-[#36302B]/10 bg-white py-4 text-[15px] font-bold text-[#36302B]"
            >
              <ArrowClockwise size={18} /> Refaire le test
            </button>
          </div>

          <p className="mt-6 text-center text-[12px] leading-relaxed text-[#36302B]/45">
            L'EPDS est un outil de repérage, pas un diagnostic. Seul un professionnel peut vous accompagner.
            Résultat enregistré uniquement sur cet appareil.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Test d'Édimbourg" back />
      <div className="px-6">
        <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-[#36302B]/8">
          <div className="h-full rounded-full bg-[#9CAEA9] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <p className="text-[13px] font-semibold uppercase tracking-wide text-[#36302B]/45">
          Question {step + 1} / {EPDS_QUESTIONS.length}
        </p>
        <p className="mt-1 text-[13px] text-[#36302B]/55">Au cours des 7 derniers jours…</p>

        <h2 key={q.id} className="soft-fade font-display mt-4 text-[22px] font-medium leading-snug text-[#36302B]">
          {q.text}
        </h2>

        <div className="mt-6 space-y-3">
          {q.options.map(([label, value], i) => (
            <button
              key={i}
              data-testid={`epds-option-${i}`}
              onClick={() => choose(value)}
              className="flex w-full items-center justify-between gap-3 rounded-3xl border py-4 pl-5 pr-4 text-left transition-all"
              style={{
                borderColor: answers[step] === value ? "#C57B67" : "rgba(54,48,43,0.1)",
                backgroundColor: answers[step] === value ? "#C57B67" : "#FFFFFF",
                color: answers[step] === value ? "#FFFFFF" : "#36302B",
              }}
            >
              <span className="text-[15px] font-semibold">{label}</span>
              <ArrowRight size={18} className="opacity-40" />
            </button>
          ))}
        </div>

        {step > 0 && (
          <button
            onClick={() => setStep(step - 1)}
            data-testid="epds-prev"
            className="mt-5 text-[14px] font-semibold text-[#36302B]/50"
          >
            ← Question précédente
          </button>
        )}
      </div>
    </div>
  );
}
